import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";

type InterfaceMode = "simple" | "advanced";

interface InterfaceModeContextType {
  mode: InterfaceMode;
  setMode: (mode: InterfaceMode) => void;
  toggleMode: () => void;
  isAdvanced: boolean;
}

const InterfaceModeContext = createContext<InterfaceModeContextType>({
  mode: "simple",
  setMode: () => {},
  toggleMode: () => {},
  isAdvanced: false,
});

export function InterfaceModeProvider({ children }: { children: ReactNode }) {
  const { effectiveRole } = useAuth();

  // Super Admin (primary_owner/owner) always advanced
  const isStaff = effectiveRole === "primary_owner" || effectiveRole === "owner";

  const [mode, setModeState] = useState<InterfaceMode>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("vazoul-interface-mode") as InterfaceMode;
      if (saved === "simple" || saved === "advanced") return saved;
    }
    return "simple";
  });

  // Only staff can access advanced mode; all other roles locked to simple
  const effectiveMode: InterfaceMode = isStaff ? mode : "simple";

  useEffect(() => {
    if (!isStaff) {
      localStorage.setItem("vazoul-interface-mode", mode);
    }
  }, [mode, isStaff]);

  const setMode = useCallback((m: InterfaceMode) => {
    // Only staff (primary_owner/owner) can switch modes
    if (isStaff) setModeState(m);
  }, [isStaff]);

  const toggleMode = useCallback(() => {
    // Only staff (primary_owner/owner) can toggle
    if (isStaff) setModeState(prev => prev === "simple" ? "advanced" : "simple");
  }, [isStaff]);

  return (
    <InterfaceModeContext.Provider value={{ mode: effectiveMode, setMode, toggleMode, isAdvanced: effectiveMode === "advanced" }}>
      {children}
    </InterfaceModeContext.Provider>
  );
}

export const useInterfaceMode = () => useContext(InterfaceModeContext);
